import React, { useState, useEffect } from "react";
import { X, CalendarDays, Users } from "lucide-react";
import leaveService from "@/services/leaveService";
import { formatDuration } from "@/utils/dateUtils";

// Deterministic avatar palette based on name (matches EmployeesOnLeave widget)
const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-purple-100 text-purple-700",
  "bg-green-100 text-green-700",
  "bg-orange-100 text-orange-700",
  "bg-pink-100 text-pink-700",
  "bg-teal-100 text-teal-700",
  "bg-indigo-100 text-indigo-700",
  "bg-rose-100 text-rose-700",
];

const getInitials = (leave) => {
  const first = leave.Employee?.User?.first_name || "";
  const last = leave.Employee?.User?.last_name || "";
  if (first || last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return "?";
};

const getAvatarColor = (leave) => {
  const name = `${leave.Employee?.User?.first_name || ""}${leave.Employee?.User?.last_name || ""}`;
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 997;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const getDaysUntilReturn = (endDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const returnDate = new Date(endDate);
  returnDate.setHours(0, 0, 0, 0);
  return Math.round((returnDate - today) / (1000 * 60 * 60 * 24));
};

const formatDateRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const options = { month: "short", day: "numeric" };
  if (start.toDateString() === end.toDateString()) {
    return start.toLocaleDateString("en-US", options);
  }
  return `${start.toLocaleDateString("en-US", options)} - ${end.toLocaleDateString("en-US", options)}`;
};

const EmployeesOnLeaveModal = ({ open, onClose, limit = 100, leaves: providedLeaves }) => {
  // Prefer data passed in by the parent (avoids a duplicate API call).
  // Falls back to self-fetching when no data is provided.
  const hasProvidedData = Array.isArray(providedLeaves);
  const [leaves, setLeaves] = useState(hasProvidedData ? providedLeaves : []);
  const [loading, setLoading] = useState(!hasProvidedData);
  const [error, setError] = useState(false);

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      setError(false);
      const response = await leaveService.getCurrentLeaves({ limit });
      // leaveService.getCurrentLeaves returns the unwrapped body, so the
      // leaves array lives at response.data.leaves.
      setLeaves(response?.data?.leaves || []);
    } catch (err) {
      console.error("Failed to fetch employees on leave:", err);
      setError(true);
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (hasProvidedData) {
      setLeaves(providedLeaves);
      setLoading(false);
      setError(false);
    } else {
      fetchLeaves();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasProvidedData, providedLeaves]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="p-6 text-white border-b"
          style={{ background: "linear-gradient(to bottom, #16314C, #0d1f30)" }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-orange-500/20 rounded-xl flex items-center justify-center">
                <CalendarDays className="h-5 w-5 text-orange-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Employees on Leave</h3>
                <p className="text-sm text-gray-300 mt-0.5">
                  {loading
                    ? "Loading..."
                    : error
                      ? "Couldn't load data"
                      : `${leaves.length} ${leaves.length === 1 ? "employee is" : "employees are"} currently on leave`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/10 rounded p-2 transition-colors"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="animate-pulse flex items-center gap-3 p-3">
                  <div className="h-11 w-11 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-12">
              <Users className="h-14 w-14 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Couldn't load employees on leave</p>
              <button
                onClick={fetchLeaves}
                className="px-4 py-2 bg-[#DF6B38] text-white rounded-lg text-sm font-medium hover:bg-[#c85f30] transition-colors"
              >
                Try again
              </button>
            </div>
          )}

          {!loading && !error && leaves.length === 0 && (
            <div className="text-center py-12">
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-green-400 rounded-full blur-md opacity-20"></div>
                <Users className="relative h-14 w-14 text-green-500 mx-auto mb-4" />
              </div>
              <p className="text-lg font-semibold text-gray-700 mb-1">
                Everyone is in the office today 🎉
              </p>
              <p className="text-sm text-gray-500">
                No employees currently on leave
              </p>
            </div>
          )}

          {!loading && !error && leaves.length > 0 && (
            <div className="space-y-3">
              {leaves.map((leave) => {
                const daysLeft = getDaysUntilReturn(leave.end_date);
                return (
                  <div
                    key={leave.id}
                    className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-orange-200 hover:shadow-md transition-all"
                  >
                    <div
                      className={`h-11 w-11 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${getAvatarColor(
                        leave,
                      )}`}
                    >
                      {getInitials(leave)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900">
                          {leave.Employee?.User
                            ? `${leave.Employee.User.first_name} ${leave.Employee.User.last_name}`
                            : "Unknown"}
                        </p>
                        <span className="flex items-center gap-1.5 text-[11px] text-gray-600 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full">
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: leave.LeaveType?.color || "#DF6B38",
                            }}
                          />
                          {leave.LeaveType?.name || leave.leave_type || "Leave"}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5 truncate">
                        {leave.Employee?.Department?.name || "Department"} •{" "}
                        {formatDuration(leave.total_days)} •{" "}
                        {formatDateRange(leave.start_date, leave.end_date)}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-medium text-gray-700">
                        Returns{" "}
                        {new Date(leave.end_date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </p>
                      <p
                        className={`text-xs ${
                          daysLeft <= 1
                            ? "text-green-600 font-medium"
                            : "text-gray-400"
                        }`}
                      >
                        {daysLeft <= 0
                          ? "today"
                          : daysLeft === 1
                            ? "tomorrow"
                            : `in ${daysLeft} days`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-orange-600 to-orange-700 text-white py-3 rounded-lg hover:from-orange-700 hover:to-orange-800 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeesOnLeaveModal;
